// voltage: okendo redeem panel and card points cost
// Costs come from Okendo's public redemption_rules; the redemption itself runs in Okendo's showcase widget.
(() => {
  if (window.VoltageOkendoRedeem) return; // the card snippet can add this script more than once
  window.VoltageOkendoRedeem = true;

  const API = "https://api.okendo.io/v1";
  const RULES_TTL = 10 * 60 * 1000; // sessionStorage cache for the rule list
  const RECHECK_GAP = 60 * 1000;
  const REDEEMED_TTL = 30 * 24 * 60 * 60 * 1000; // points are already spent, so Add to cart must outlive the session
  const MATCH_TIMEOUT = 20000; // give Okendo's widget this long to render anything before falling back
  const SHOWCASE = "[data-oke-loyalty-free-product-showcase]";
  const CARD = ".c-customisableActionCard";

  const stripId = (id) => String(id || "").replace(/^shopify-/, "").replace(/^gid:\/\/shopify\/Product\//, "");
  const fmt = (n) => new Intl.NumberFormat(document.documentElement.lang || "en").format(n);
  const warn = (...a) => console.warn("[voltage-okendo-redeem]", ...a);

  function subscriberId(fallback) {
    try {
      const el = document.getElementById("oke-loyalty-settings");
      const settings = el ? JSON.parse(el.textContent) : null;
      return settings?.subscriberId || fallback || "";
    } catch (_e) {
      return fallback || "";
    }
  }

  let rulesPromise = null;
  let rulesFresh = false;
  function getRules(sub, currency) {
    if (rulesPromise) return rulesPromise;
    if (!sub) return Promise.reject(new Error("No Okendo subscriber id on the page"));
    const key = `voltage-okendo-rules:${sub}:${currency || ""}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || "null");
      if (cached && Date.now() - cached.t < RULES_TTL) return (rulesPromise = Promise.resolve(cached.rules));
    } catch (_e) {
      // storage blocked; fetch every time
    }
    rulesPromise = (async () => {
      const url = `${API}/stores/${encodeURIComponent(sub)}/loyalty/redemption_rules${currency ? `?currencyCode=${encodeURIComponent(currency)}` : ""}`;
      // No okendo-api-version header: from a browser it forces a CORS preflight that Okendo rejects.
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Okendo redemption_rules ${res.status}`);
      const rules = (await res.json())?.redemptionRules || [];
      rulesFresh = true;
      try {
        sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), rules }));
      } catch (_e) {
        // storage blocked
      }
      return rules;
    })();
    rulesPromise.catch(() => {
      rulesPromise = null;
    });
    return rulesPromise;
  }

  const freeProductRule = (rules, productId) =>
    rules.find((r) => r?.type === "free-product" && stripId(r.reward?.product?.productId) === String(productId)) || null;

  // a product missing from the saved list gets one fresh look, so a reward added minutes ago shows up
  async function freshRules(sub, currency) {
    const key = `voltage-okendo-recheck:${sub}`;
    try {
      if (rulesFresh || Date.now() - Number(sessionStorage.getItem(key) || 0) < RECHECK_GAP) return null;
      sessionStorage.setItem(key, String(Date.now()));
      sessionStorage.removeItem(`voltage-okendo-rules:${sub}:${currency || ""}`);
    } catch (_e) {
      return null;
    }
    rulesPromise = null;
    return getRules(sub, currency);
  }

  // pdp panel

  class VoltageOkendoRedeem extends HTMLElement {
    #started = false;
    #observer = null;
    #cardObserver = null;
    #timer = null;
    #lastMoreClick = 0;
    #sawUnredeemed = false;
    #rule = null;
    #showcaseStarted = false;
    #cartTimer = null;

    connectedCallback() {
      if (this.#started) return;
      this.#started = true;
      // every variant picker on the page fires this, so it is filtered to this product
      document.addEventListener("variant:changed", this.#onVariantChanged);
      this.querySelector("[data-add-to-cart]")?.addEventListener("click", () => this.#addToCart());
      // cart rules: re-check when the cart changes (cart:update = any change, cart:updated = after a re-render)
      document.addEventListener("cart:update", this.#onCartChanged);
      document.addEventListener("cart:updated", this.#onCartChanged);
      this.#run();
    }

    disconnectedCallback() {
      document.removeEventListener("variant:changed", this.#onVariantChanged);
      document.removeEventListener("cart:update", this.#onCartChanged);
      document.removeEventListener("cart:updated", this.#onCartChanged);
      clearTimeout(this.#cartTimer);
      this.#observer?.disconnect();
      this.#cardObserver?.disconnect();
      clearTimeout(this.#timer);
    }

    #onVariantChanged = (event) => {
      const variant = event.detail?.variant;
      if (!variant?.id) return;
      if (variant.product_id && String(variant.product_id) !== String(this.#productId)) return;
      const button = this.querySelector("[data-add-to-cart]");
      if (button) button.dataset.variantId = String(variant.id);
      if (this.dataset.state === "redeemed") this.#syncAddButton();
    };

    get #productId() {
      return this.dataset.productId;
    }

    #setState(state) {
      this.dataset.state = state;
      this.#log(`state → ${state}`);
    }

    // ?voltage-debug=1 renders a log inside the block
    #log(message) {
      if (!new URLSearchParams(location.search).has("voltage-debug")) return;
      let pre = this.querySelector("[data-role=\"debug\"]");
      if (!pre) {
        pre = document.createElement("pre");
        pre.dataset.role = "debug";
        pre.style.cssText = "display:block;white-space:pre-wrap;font:12px/1.4 monospace;background:#111;color:#0f0;padding:8px;margin-top:8px;max-height:260px;overflow:auto";
        this.appendChild(pre);
      }
      pre.textContent += `${((performance.now() / 1000).toFixed(1) + "s").padStart(6)} ${message}\n`;
    }

    // [points] in each [data-text] becomes the cost, or the shortfall for data-points="needed".
    #fill(cost, needed = 0) {
      for (const el of this.querySelectorAll("[data-text]")) {
        const n = el.dataset.points === "needed" ? needed : cost;
        el.textContent = el.dataset.text.replace(/\[points\]/g, fmt(n));
      }
    }

    #redeemedKey() {
      return `voltage-okendo-redeemed:${this.#productId}`;
    }

    #recentlyRedeemed() {
      try {
        const t = Number(localStorage.getItem(this.#redeemedKey()) || sessionStorage.getItem(this.#redeemedKey()) || 0);
        return t && Date.now() - t < REDEEMED_TTL;
      } catch (_e) {
        return false;
      }
    }

    async #run() {
      const design = this.dataset.designMode === "true";
      let rule = null;
      let loaded = false;
      try {
        const sub = subscriberId(this.dataset.subscriberId);
        rule = freeProductRule(await getRules(sub, this.dataset.currency), this.#productId);
        if (!rule && !design) {
          const fresh = await freshRules(sub, this.dataset.currency);
          if (fresh) rule = freeProductRule(fresh, this.#productId);
        }
        loaded = true;
      } catch (error) {
        warn(error?.message || error);
      }

      if (design) {
        this.#fill(rule?.reward?.points ?? 600, 150);
        this.#setState("preview");
        return;
      }
      this.#log(`rules loaded — match for product ${this.#productId}: ${rule ? "yes" : "NO"}`);
      if (!rule) {
        // no public reward for this product yet; tier-limited ones land here too
        if (loaded) this.#setState("coming-soon");
        else this.#setState(this.dataset.loggedIn === "true" ? "fallback" : "unavailable");
        return;
      }

      const cost = Number(rule.reward?.points) || 0;
      this.#fill(cost);
      this.#log(`rule ${rule.redemptionRuleId} "${rule.title}" cost ${cost} | loggedIn ${this.dataset.loggedIn} | balance ${this.dataset.balance} | recentlyRedeemed ${this.#recentlyRedeemed()}`);

      if (this.#recentlyRedeemed()) {
        this.#markRedeemed(false);
        return;
      }
      if (this.dataset.loggedIn !== "true") {
        this.#setState("login");
        return;
      }
      const balance = Number(this.dataset.balance) || 0;
      if (balance < cost) {
        this.#fill(cost, cost - balance);
        this.#setState("short");
        return;
      }

      this.#rule = rule;
      await this.#evaluateCart();
    }

    // cart rules
    // Redeem needs a paid product in the cart ("need-paid") and no reward already in it ("one-only").

    #onCartChanged = () => {
      if (this.dataset.state === "redeemed") {
        clearTimeout(this.#cartTimer);
        this.#cartTimer = setTimeout(() => this.#syncAddButton(), 300);
        return;
      }
      if (!["need-paid", "one-only", "ready"].includes(this.dataset.state)) return;
      clearTimeout(this.#cartTimer);
      this.#cartTimer = setTimeout(() => this.#evaluateCart(), 300);
    };

    async #cartState() {
      const cart = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, { headers: { Accept: "application/json" } }).then((r) => r.json());
      const type = (this.dataset.rewardType || "Reward").trim().toLowerCase();
      let rewardUnits = 0;
      let paidLines = 0;
      for (const item of cart?.items || []) {
        if (String(item.product_type || "").trim().toLowerCase() === type) rewardUnits += item.quantity;
        else paidLines += 1;
      }
      return { rewardUnits, paidLines };
    }

    async #evaluateCart() {
      let next = "ready";
      if (this.dataset.cartRules !== "false") {
        try {
          const { rewardUnits, paidLines } = await this.#cartState();
          if (paidLines === 0) next = "need-paid";
          else if (rewardUnits > 0) next = "one-only";
          this.#log(`cart: ${paidLines} paid line(s), ${rewardUnits} reward unit(s) → ${next}`);
        } catch (_e) {
          // cart unreadable; don't block on our own rule
        }
      }
      if (this.dataset.state === "redeemed") return; // a redemption landed while we were checking
      if (next !== "ready") {
        this.#setState(next);
        return;
      }
      this.#setState("ready");
      if (!this.#showcaseStarted) {
        this.#showcaseStarted = true;
        this.#watchRedeemCalls();
        this.#filterShowcase(this.#rule);
      }
    }

    // Trim Okendo's showcase to this product's card, matched by product link, then rule title,
    // then image file.
    #filterShowcase(rule) {
      const host = document.querySelector(SHOWCASE);
      this.#log(`showcase host ${host ? "found" : "MISSING"}`);
      if (!host) {
        warn("No Okendo showcase block on this template — add Okendo's Loyalty Product Showcase below ⚡ Okendo Redeem.");
        this.#setState("fallback");
        return;
      }
      host.setAttribute("data-voltage-redeem", "pending");
      let matchedCard = null;
      let unmatchedSince = 0;
      let recheck = null;

      // Runs for the life of the page: Okendo re-renders the list once it knows who is
      // logged in, replacing the card we tagged.
      const apply = () => {
        const cards = [...host.querySelectorAll(CARD)];
        if (cards.length === 0) {
          this.#log(`showcase: 0 cards (host children ${host.children.length}, skeletons ${host.querySelectorAll(".c-customisableSkeletonCard").length})`);
          return; // skeletons or mid-render; keep the current state
        }
        const match = cards.find((card) => this.#cardMatches(card, rule));
        this.#log(`showcase: ${cards.length} cards → match ${match ? "YES" : "no"}; first: ${cards.slice(0, 3).map((c) => `"${c.querySelector(".c-customisableActionCard-titleArea-title")?.textContent.trim() || "?"}"${c.querySelector("a[href]") ? " +link" : ""}`).join(", ")}`);
        if (match) {
          unmatchedSince = 0;
          for (const card of cards) card.toggleAttribute("data-voltage-match", card === match);
          if (host.getAttribute("data-voltage-redeem") !== "matched") host.setAttribute("data-voltage-redeem", "matched");
          if (this.dataset.state === "fallback") this.#setState("ready");
          if (match !== matchedCard) {
            matchedCard = match;
            this.#watchCard(match);
          }
          return;
        }
        matchedCard = null;
        // Paginated list: load the next page and let the observer re-check.
        const more = [...host.querySelectorAll("button")].find((b) => /^(show|load) more$/i.test(b.textContent.trim()));
        if (more && !more.disabled && Date.now() - this.#lastMoreClick > 800) {
          this.#lastMoreClick = Date.now();
          more.click();
          return;
        }
        // No card for this product. Re-renders pass through this briefly, so hold 1.5s first.
        if (!unmatchedSince) {
          unmatchedSince = Date.now();
          clearTimeout(recheck);
          recheck = setTimeout(apply, 1600);
        } else if (Date.now() - unmatchedSince > 1500 && host.getAttribute("data-voltage-redeem") !== "unmatched") {
          host.setAttribute("data-voltage-redeem", "unmatched");
          warn("Okendo's showcase has no card for this product — showing the fallback link.");
          this.#setState("fallback");
        }
      };

      apply();
      let scheduled = false;
      this.#observer = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          apply();
        });
      });
      this.#observer.observe(host, { childList: true, subtree: true, characterData: true });
      // Nothing rendered in time: fall back, but keep observing in case it turns up late.
      this.#timer = setTimeout(() => {
        if (host.getAttribute("data-voltage-redeem") === "pending") {
          host.setAttribute("data-voltage-redeem", "unmatched");
          warn("Okendo's showcase didn't render in time — showing the fallback link.");
          this.#setState("fallback");
        }
      }, MATCH_TIMEOUT);
    }

    #cardMatches(card, rule) {
      const handle = this.dataset.productHandle;
      if (handle && card.querySelector(`a[href*="/products/${handle}"]`)) return true;
      const title = card.querySelector(".c-customisableActionCard-titleArea-title")?.textContent?.trim();
      if (title && rule.title && title === rule.title.trim()) return true;
      const bg = card.querySelector(".c-customisableActionCard-image")?.style?.backgroundImage || "";
      const files = [rule.imageUrl, rule.reward?.product?.imageUrl]
        .filter(Boolean)
        .map((u) => u.split("?")[0].split("/").pop())
        .filter(Boolean);
      return files.some((f) => bg.includes(f));
    }

    // Signal 1: the reply to Okendo's redeem call. Only this product's card is visible,
    // so a success here is this product.
    #watchRedeemCalls() {
      if (!window.__voltageOkendoRedeemHooks) {
        window.__voltageOkendoRedeemHooks = new Set();
        const original = window.fetch;
        window.fetch = function (input, init) {
          const promise = original.apply(this, arguments);
          try {
            const url = typeof input === "string" ? input : input?.url || "";
            const method = (init?.method || input?.method || "GET").toUpperCase();
            if (method === "POST" && /api\.okendo\.io\/v1\/loyalty\/redemptions/.test(url)) {
              promise
                .then((res) => {
                  if (!res.ok) return;
                  res
                    .clone()
                    .json()
                    .then((data) => {
                      for (const fn of window.__voltageOkendoRedeemHooks) fn(data);
                    })
                    .catch(() => {});
                })
                .catch(() => {});
            }
          } catch (_e) {
            // never let the watcher break a fetch
          }
          return promise;
        };
      }
      window.__voltageOkendoRedeemHooks.add((data) => {
        this.#log(`Okendo redemptions reply: ${JSON.stringify(data?.reward || {}).slice(0, 120)}`);
        if (data?.reward?.result?.success !== false) this.#markRedeemed(true);
      });
    }

    // Signal 2: the matched card flips to "Redeemed" or shows Okendo's success message.
    #watchCard(card) {
      const redeemedNow = () => {
        const button = [...card.querySelectorAll("button")].find((b) => /^redeemed$/i.test(b.textContent.trim()));
        const alert = card.querySelector(".c-customisableActionCard-alertContainer")?.textContent || "";
        return Boolean(button) || /reward redeemed/i.test(alert);
      };
      this.#cardObserver?.disconnect();
      if (redeemedNow()) {
        // Already redeemed on load means an earlier redemption, unless we saw an un-redeemed
        // copy of this card before Okendo swapped it.
        if (this.#sawUnredeemed) this.#markRedeemed(true);
        return;
      }
      this.#sawUnredeemed = true;
      this.#log("watching the matched card for a Redeemed state");
      this.#cardObserver = new MutationObserver(() => {
        if (redeemedNow()) {
          this.#cardObserver.disconnect();
          this.#markRedeemed(true);
        }
      });
      this.#cardObserver.observe(card, { childList: true, subtree: true, characterData: true });
    }

    #markRedeemed(fresh) {
      if (this.dataset.state === "redeemed") return;
      this.#setState("redeemed");
      if (fresh) {
        try {
          localStorage.setItem(this.#redeemedKey(), String(Date.now()));
        } catch (_e) {
          // storage blocked; the button still shows for this page view
        }
      }
      // Okendo can add the product to the cart itself after redeeming, so re-check a few times.
      this.#syncAddButton();
      for (const ms of [1500, 4000, 8000]) setTimeout(() => this.#syncAddButton(), ms);
    }

    // add to cart (redeemed state only)
    // One unit per order: enabled only when /cart.js shows the variant isn't in the cart,
    // and re-checked right before the add.

    #setAddButton(state, text) {
      const button = this.querySelector("[data-add-to-cart]");
      if (!button) return;
      const label = button.querySelector(".btn__text") || button;
      // the "Redeemed!" line follows the cart too: "add it" vs "it's in your cart"
      const line = this.querySelector("[data-redeemed-line]");
      if (line) line.textContent = (text === button.dataset.textInCart ? line.dataset.textInCart : line.dataset.textAdd) || line.textContent;
      if (state === "ready") {
        button.disabled = false;
        button.removeAttribute("aria-disabled");
        button.classList.remove("btn--loading");
        label.textContent = text || button.dataset.textAdd;
      } else {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
        button.classList.toggle("btn--loading", state === "loading");
        if (state !== "loading") label.textContent = text;
      }
    }

    async #variantInCart(variantId) {
      const cart = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, { headers: { Accept: "application/json" } }).then((r) => r.json());
      return (cart?.items || []).some((item) => String(item.variant_id) === String(variantId));
    }

    async #syncAddButton() {
      const button = this.querySelector("[data-add-to-cart]");
      if (!button) return;
      try {
        const inCart = await this.#variantInCart(button.dataset.variantId);
        this.#setAddButton(inCart ? "done" : "ready", inCart ? button.dataset.textInCart : button.dataset.textAdd);
      } catch (_e) {
        this.#setAddButton("ready");
      }
    }

    // Same request the theme's product form sends (assets/product-form.js): /cart/add with the
    // cart drawer's section ids, then the theme's cart:update event with the returned sections so
    // the drawer morphs and opens (assets/cart-drawer.js).
    async #addToCart() {
      const button = this.querySelector("[data-add-to-cart]");
      const error = this.querySelector("[data-add-error]");
      if (!button || button.disabled || this.dataset.state !== "redeemed") return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;
      this.#setAddButton("loading");
      error?.classList.add("hidden");

      // never a second unit: re-check the live cart first
      try {
        if (await this.#variantInCart(variantId)) {
          this.#setAddButton("done", button.dataset.textInCart);
          return;
        }
      } catch (_e) {
        // cart unreadable; let /cart/add decide
      }

      const sections = [];
      document.dispatchEvent(new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } }));
      const body = new FormData();
      body.append("id", variantId);
      body.append("quantity", "1");
      if (sections.length) body.append("sections", [...new Set(sections)].join(","));

      let response;
      try {
        response = await fetch(window.FoxTheme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || "/"}cart/add`, {
          method: "POST",
          body,
          headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/javascript" },
        }).then((r) => r.json());
      } catch (_e) {
        response = { status: 500, message: "Something went wrong — please try again." };
      }
      this.#log(`cart/add → ${response?.status ? `error ${response.status}: ${response.description || response.message}` : "added"}`);

      if (response?.status) {
        this.#setAddButton("ready");
        if (error) {
          error.textContent = response.description || response.message || "Could not add this product to your cart.";
          error.classList.remove("hidden");
        }
        document.dispatchEvent(new CustomEvent("product-ajax:error", { detail: { errorMessage: response.description || response.message } }));
        return;
      }

      this.#setAddButton("done", button.dataset.textAdded);
      const detail = {
        resource: response,
        sourceId: this.id || "voltage-okendo-redeem",
        data: {
          source: "voltage-okendo-redeem",
          itemCount: 1,
          isIncremental: true,
          productId: this.#productId,
          variantId: String(variantId),
          sections: response.sections,
        },
      };
      document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent("product-ajax:added", { detail: { product: response } }));
    }
  }

  if (!customElements.get("voltage-okendo-redeem")) customElements.define("voltage-okendo-redeem", VoltageOkendoRedeem);

  // points cost on reward product cards
  // <span data-okendo-points-for="<product id>" data-text="[points] points" hidden> per card.

  let cardsQueued = false;
  async function fillCards() {
    cardsQueued = false;
    const spans = [...document.querySelectorAll("[data-okendo-points-for]:not([data-filled])")];
    if (spans.length === 0) return;
    let rules;
    const sub = subscriberId(document.querySelector("voltage-okendo-redeem")?.dataset.subscriberId);
    const currency = window.Shopify?.currency?.active;
    try {
      rules = await getRules(sub, currency);
      if (spans.some((span) => !freeProductRule(rules, span.dataset.okendoPointsFor))) rules = (await freshRules(sub, currency)) || rules;
    } catch (error) {
      warn(error?.message || error);
      return;
    }
    for (const span of spans) {
      span.dataset.filled = "true";
      const rule = freeProductRule(rules, span.dataset.okendoPointsFor);
      if (!rule && !span.dataset.textNone) continue;
      span.textContent = rule ? (span.dataset.text || "[points] points").replace(/\[points\]/g, fmt(rule.reward.points)) : span.dataset.textNone;
      span.hidden = false;
      span.closest(".product-card")?.setAttribute("data-okendo-points", "");
    }
  }
  const queueCards = () => {
    if (cardsQueued) return;
    cardsQueued = true;
    setTimeout(fillCards, 50);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", queueCards);
  else queueCards();
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.("[data-okendo-points-for]") || n.querySelector?.("[data-okendo-points-for]"))))) queueCards();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
